import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useProfile } from "../hooks/useProfile";
import Select from "react-select";
import { User, Mail, Phone } from "lucide-react";
import { toast } from "react-toastify";

function Profile() {
  const { fetchProfile, updateProfile } = useProfile();
  const [editMode, setEditMode] = useState(false);

  const {
    register,
    control,
    reset,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      full_name: "",
      email: "",
      user_type: "",
      phone: "",
      country_code: "",
    },
  });

  const [profileLoaded, setProfileLoaded] = React.useState(false);

  useEffect(() => {
    async function loadProfile() {
      try {
        const response = await fetchProfile();
        if (response?.data) {
          reset({
            full_name: response.data.full_name || "",
            email: response.data.email || "",
            user_type: response.data.user_type || "",
            phone: response.data.phone || "",
            country_code: response.data.country_code || "",
          });
          setProfileLoaded(true);
        }
      } catch (error) {
        console.error("Failed to load profile", error);
      }
    }
    if (!profileLoaded) {
      loadProfile();
    }
  }, [profileLoaded, reset, fetchProfile]);

  const userTypeOptions = [
    { value: "admin", label: "Admin" },
    { value: "sub_admin", label: "Sub Admin" },
    { value: "user", label: "User" },
  ];

  const onSubmit = async (data) => {
    try {
      // Include _id in the data sent to updateProfile
      const profileData = { ...data, _id: fetchedProfileId };
      await updateProfile(profileData);
      toast.success("Profile updated successfully");
      setEditMode(false);
      reset(data);
    } catch (error) {
      toast.error("Failed to update profile");
      console.error(error);
    }
  };

  // Add state to store fetched profile id
  const [fetchedProfileId, setFetchedProfileId] = React.useState(null);

  useEffect(() => {
    async function loadProfile() {
      try {
        const response = await fetchProfile();
        if (response?.data) {
          reset({
            full_name: response.data.full_name || "",
            email: response.data.email || "",
            user_type: response.data.user_type || "",
            phone: response.data.phone || "",
            country_code: response.data.country_code || "",
          });
          setFetchedProfileId(response.data._id || null);
          setProfileLoaded(true);
        }
      } catch (error) {
        console.error("Failed to load profile", error);
      }
    }
    if (!profileLoaded) {
      loadProfile();
    }
  }, [profileLoaded, reset, fetchProfile]);

  return (
    <div className="max-w-xl mx-auto p-6 bg-white rounded shadow">
      <h1 className="text-2xl font-bold mb-6">Profile</h1>
      <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
        {/* Full Name */}
        <div>
          <label className="block text-sm font-medium mb-1">Full Name</label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              {...register("full_name")}
              readOnly={!editMode}
              className={`pl-10 pr-3 py-2 border rounded w-full ${
                !editMode ? "bg-gray-100 cursor-not-allowed" : ""
              }`}
            />
          </div>
          {errors.full_name && (
            <p className="text-red-500 text-xs">{errors.full_name.message}</p>
          )}
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-medium mb-1">Email</label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              {...register("email")}
              readOnly={!editMode}
              className={`pl-10 pr-3 py-2 border rounded w-full ${
                !editMode ? "bg-gray-100 cursor-not-allowed" : ""
              }`}
            />
          </div>
          {errors.email && (
            <p className="text-red-500 text-xs">{errors.email.message}</p>
          )}
        </div>

        {/* User Type */}
        <div>
          <label className="block text-sm font-medium mb-1">User Type</label>
          <Controller
            name="user_type"
            control={control}
            render={({ field }) => {
              const selectedOption =
                userTypeOptions.find((opt) => opt.value === field.value) || null;
              return (
                <Select
                  {...field}
                  value={selectedOption}
                  options={userTypeOptions}
                  isDisabled={true}
                  className="w-full"
                />
              );
            }}
          />
        </div>

        {/* Country Code */}
        <div>
          <label className="block text-sm font-medium mb-1">Country Code</label>
          <input
            {...register("country_code")}
            readOnly={!editMode}
            className={`pl-3 pr-3 py-2 border rounded w-full ${
              !editMode ? "bg-gray-100 cursor-not-allowed" : ""
            }`}
          />
        </div>

        {/* Phone */}
        <div>
          <label className="block text-sm font-medium mb-1">Phone</label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              {...register("phone")}
              readOnly={!editMode}
              className={`pl-10 pr-3 py-2 border rounded w-full ${
                !editMode ? "bg-gray-100 cursor-not-allowed" : ""
              }`}
            />
          </div>
          {errors.phone && (
            <p className="text-red-500 text-xs">{errors.phone.message}</p>
          )}
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t">
          {editMode ? (
            <>
              <button
                type="button"
                onClick={() => {
                  setEditMode(false);
                  reset();
                }}
                className="px-4 py-2 border rounded text-gray-700"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 bg-primary text-white rounded flex items-center justify-center gap-2"
              >
                {isSubmitting ? "Saving..." : "Save Changes"}
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => setEditMode(true)}
              className="px-4 py-2 bg-primary text-white rounded"
            >
              Edit Profile
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

export default Profile;
